import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { User } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import DashboardLayout from '../Pages/DashboardLayout';
import { routeConfig, getRouteFromTab, getTabFromRoute } from '../../routes';
import { useNotification } from '@/components/context/NotificationContext';
import { api } from '../../utils/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
  currentUser: User | null;
  allowedRoles?: string[];
  allowedTeamRoles?: string[];
  allowAdminOverride?: boolean;
}

const normalize = (value?: string) => (value || '').trim().toLowerCase();

export const ProtectedRoute = ({
  children,
  currentUser,
  allowedRoles,
  allowedTeamRoles,
  allowAdminOverride = true,
}: ProtectedRouteProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { unreadCount } = useNotification();
  const [verificationCheckLoading, setVerificationCheckLoading] = useState(false);
  const [verificationBlocked, setVerificationBlocked] = useState(false);

  const currentRole = normalize(currentUser?.role);
  const currentTeamRole = normalize(currentUser?.team_role);

  const roleRuleDefined = Array.isArray(allowedRoles) && allowedRoles.length > 0;
  const teamRoleRuleDefined = Array.isArray(allowedTeamRoles) && allowedTeamRoles.length > 0;

  const hasAllowedRole =
    !roleRuleDefined ||
    allowedRoles.some((role) => normalize(role) === currentRole);

  const hasAllowedTeamRole =
    !teamRoleRuleDefined ||
    allowedTeamRoles.some((teamRole) => normalize(teamRole) === currentTeamRole);

  const isAdmin = currentRole === 'admin';
  const matchesRules =
    roleRuleDefined && teamRoleRuleDefined
      ? (hasAllowedRole || hasAllowedTeamRole)
      : (hasAllowedRole && hasAllowedTeamRole);

  const isAuthorized =
    (allowAdminOverride && isAdmin) ||
    matchesRules;

  const handleTabChange = (tab: string) => {
    const route = getRouteFromTab(tab, currentUser?.role);
    navigate(route);
  };
//hardcoded
  const handleLogout = async () => {
    console.log('🚪 Logout icon clicked - logging out user');
    console.log('Current URL before logout:', window.location.href);
    try {
      await logout();
      console.log('✅ Logout successful - redirecting to parent site login');
      window.location.href = `${import.meta.env.VITE_HRMS_FRONTEND_URL}/login`;
    } catch (error) {
      console.error('❌ Logout failed:', error);
      // Still redirect to parent site login even if logout fails
      console.log('Redirecting to parent site login after error');
      window.location.href = `${import.meta.env.VITE_HRMS_FRONTEND_URL}/login`;
    }
  };

  const handleProfileClick = () => {
    navigate(routeConfig.profile);
  };

  const activeTab = getTabFromRoute(location.pathname);

  const isJoiner = currentRole === 'joiner';
  const isVerificationRoute = location.pathname === routeConfig.verification;

  useEffect(() => {
    const checkJoinerVerificationGate = async () => {
      if (!isJoiner || isVerificationRoute) {
        setVerificationBlocked(false);
        return;
      }

      try {
        setVerificationCheckLoading(true);
        const response = await api.get('/onboarding-verification');
        const checklist = Array.isArray(response?.data) ? response.data : [];

        // Do not block when HR has not created a checklist for this joiner yet.
        if (checklist.length === 0) {
          setVerificationBlocked(false);
          return;
        }

        // Gate on joiner side only: unlock other pages once the joiner has
        // answered YES on all mandatory items. HR/management PENDING does not block access.
        const mandatoryItems = checklist.filter(
          (item: any) => item?.mandatory !== false && item?.phase !== 'DEMO_SCHEDULE'
        );

        const joinerSideComplete =
          mandatoryItems.length === 0 ||
          mandatoryItems.every((item: any) => item?.joinerDecision === 'YES');

        setVerificationBlocked(!joinerSideComplete);
      } catch (error) {
        console.error('Verification gate check failed:', error);
        // On API failure, keep existing behavior and do not hard-block.
        setVerificationBlocked(false);
      } finally {
        setVerificationCheckLoading(false);
      }
    };

    checkJoinerVerificationGate();
  }, [isJoiner, isVerificationRoute, location.pathname]);

  if (verificationCheckLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto" />
          <p className="mt-3 text-sm text-gray-600">Checking verification status...</p>
        </div>
      </div>
    );
  }

  if (isJoiner && verificationBlocked && !isVerificationRoute) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-md flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-xl border bg-white shadow-2xl p-6 space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">Verification Required</h2>
          <p className="text-sm text-slate-600">
            Please submit your answers on all mandatory verification items before accessing other pages.
          </p>
          <p className="text-xs text-slate-500">
            After you submit, you can use the rest of the portal while management review is pending.
            Demo Schedule Milestones are not required for this check.
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate(routeConfig.verification)}
              className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Go to Verification
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to={routeConfig.login} replace />;
  }

  if (!isAuthorized) {
    return <Navigate to={routeConfig.dashboard} replace state={{ from: location.pathname }} />;
  }

  return (
    <DashboardLayout
      currentUser={currentUser}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      onLogout={handleLogout}
      unreadNotifications={unreadCount}
      onProfileClick={handleProfileClick}
    >
      {children}
    </DashboardLayout>
  );
};
